import React from 'react';
import {
  number, string, bool
} from 'prop-types';
import { Translate } from 'react-localize-redux';
import { localizedType } from '../../../constants/types';
import styles from './variableSummaryLegend.module.css';
import { getTranslation, withLang, langType } from '../../../i18n';
import { toLocaleNumber } from '../../../util';

const formatFeedback = (userAverage, average, feedback, lang) => {
  const feedbackText = getTranslation(feedback, lang);

  if (feedbackText) {
    return feedbackText
      .replace(/{{\s*USER_RESULT\s*}}/g, toLocaleNumber(userAverage, lang))
      .replace(/{{\s*GROUP_RESULT\s*}}/g, toLocaleNumber(average, lang));
  }

  return feedbackText;
};

const VariableSummaryLegend = ({
  feedback,
  userValue,
  userKey,
  groupValue,
  groupKey,
  lang,
  standardDeviation,
  showStandardDeviation,
  showAverageIndicator,
  minAverage,
  maxAverage,
  translate: t
}) => {
  const localizeNumber = (value) => toLocaleNumber(value, lang);

  const getUserValue = (showIndicator) => {
    const legendDescriptionStyles = showIndicator
      ? `${styles.legendDescription} ${styles.legendDescriptionUser}`
      : `${styles.legendDescription}`;

    return (
      <div className={styles.legendItem}>
        <div className={styles.legendValue} aria-label={t(userKey)}>
          <p>{localizeNumber(userValue)}</p>
        </div>
        <div className={legendDescriptionStyles} aria-hidden="true">
          <p><Translate id={`${userKey}`} /></p>
        </div>
      </div>
    );
  };

  const getStandardDeviation = () => (
    <div className={styles.legendItem}>
      <div className={styles.legendValue}>
        <p>{localizeNumber(standardDeviation)}</p>
      </div>
      <div className={styles.legendDescription}>
        <p><Translate id="surveySummary.standardDeviation" /></p>
        <div className={styles.minMax}>
          <p className={styles.minMaxValue}>
            <Translate id="surveyStatistics.smallest" />: {localizeNumber(minAverage)}
            <Translate id="surveyStatistics.largest" />: {localizeNumber(maxAverage)}
          </p>
        </div>
      </div>
    </div>
  );

  const legendDescriptionStyles = showAverageIndicator
    ? `${styles.legendDescription} ${styles.legendDescriptionGroup}`
    : `${styles.legendDescription}`;

  return (
    <div>
      <div className={styles.legend}>
        {userValue !== null ? getUserValue(showAverageIndicator) : ''}
        <div className={styles.legendItem}>
          <div className={styles.legendValue} aria-label={t(groupKey)}>
            <p>{localizeNumber(groupValue)}</p>
          </div>
          <div className={legendDescriptionStyles} aria-hidden="true">
            <p><Translate id={groupKey} /></p>
          </div>
        </div>
        {showStandardDeviation && standardDeviation !== null ? getStandardDeviation() : ''}
      </div>
      <div className={`textarea ${styles.text}`}>
        <div
          dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
            __html: formatFeedback(userValue, groupValue, feedback, lang)
          }}
        />
      </div>
    </div>
  );
};

VariableSummaryLegend.propTypes = {
  userValue: number,
  userKey: string.isRequired,
  groupValue: number.isRequired,
  groupKey: string.isRequired,
  feedback: localizedType,
  standardDeviation: number,
  showStandardDeviation: bool,
  showAverageIndicator: bool,
  minAverage: number,
  maxAverage: number,
  ...langType
};

VariableSummaryLegend.defaultProps = {
  standardDeviation: null,
  feedback: null,
  userValue: null,
  showStandardDeviation: false,
  showAverageIndicator: true,
  minAverage: null,
  maxAverage: null
};

export default withLang(VariableSummaryLegend);
